/**
 * Equipment inventory, seeded for MyFitness Galerija Centrs (Rīga, 5th floor,
 * entrance on Rīdzenes iela).
 *
 * The club's own site is the source of truth and it changes; MyFitness kit out
 * primarily with Technogym, with Life Fitness, Hammer Strength and Gym80/Matrix
 * across the chain, and Galerija Centrs specifically has a Precor "Booty Zone"
 * and a recovery zone (HydroMassage, CryoLounge, RollShaper, Cloudboots,
 * Theragun).
 *
 * So this list is a *starting guess*, not gospel. Every item is toggleable in
 * Settings → Equipment. Whatever you switch off disappears from every
 * recommendation the planner makes, so spend ten minutes walking the floor once
 * and the app gets much sharper.
 */

export type EquipmentCategory =
  | 'bar'
  | 'freeweight'
  | 'bench'
  | 'rack'
  | 'machine'
  | 'cable'
  | 'functional'
  | 'cardio'
  | 'recovery';

export interface Equipment {
  id: string;
  name: string;
  category: EquipmentCategory;
  /** Whether we expect this to exist at the club by default. */
  defaultAvailable: boolean;
  note?: string;
}

export const EQUIPMENT: Equipment[] = [
  // ---- Bars ----
  { id: 'barbell', name: 'Olympic barbell (20 kg)', category: 'bar', defaultAvailable: true },
  { id: 'ez_bar', name: 'EZ curl bar', category: 'bar', defaultAvailable: true },
  { id: 'fixed_barbells', name: 'Fixed-weight barbells', category: 'bar', defaultAvailable: true },
  { id: 'trap_bar', name: 'Trap / hex bar', category: 'bar', defaultAvailable: false, note: 'Not all clubs have one — check' },

  // ---- Free weights ----
  { id: 'dumbbells', name: 'Dumbbells', category: 'freeweight', defaultAvailable: true, note: 'Typically 1–50 kg' },
  { id: 'kettlebells', name: 'Kettlebells', category: 'freeweight', defaultAvailable: true },
  { id: 'plates', name: 'Weight plates', category: 'freeweight', defaultAvailable: true },

  // ---- Benches & racks ----
  { id: 'flat_bench', name: 'Flat bench', category: 'bench', defaultAvailable: true },
  { id: 'adjustable_bench', name: 'Adjustable / incline bench', category: 'bench', defaultAvailable: true },
  { id: 'decline_bench', name: 'Decline bench', category: 'bench', defaultAvailable: true },
  { id: 'preacher_bench', name: 'Preacher curl bench', category: 'bench', defaultAvailable: true },
  { id: 'back_extension', name: '45° back extension / hyperextension', category: 'bench', defaultAvailable: true },
  { id: 'ab_bench', name: 'Decline ab bench', category: 'bench', defaultAvailable: true },
  { id: 'power_rack', name: 'Power rack / squat rack', category: 'rack', defaultAvailable: true },
  { id: 'smith_machine', name: 'Smith machine', category: 'rack', defaultAvailable: true },
  { id: 'pullup_bar', name: 'Pull-up bar', category: 'rack', defaultAvailable: true },
  { id: 'dip_station', name: 'Dip station / parallel bars', category: 'rack', defaultAvailable: true },
  { id: 'captains_chair', name: "Captain's chair / roman chair", category: 'rack', defaultAvailable: true },

  // ---- Machines ----
  { id: 'chest_press_machine', name: 'Chest press machine', category: 'machine', defaultAvailable: true },
  { id: 'incline_press_machine', name: 'Incline press machine', category: 'machine', defaultAvailable: true, note: 'Hammer Strength plate-loaded at many clubs' },
  { id: 'pec_deck', name: 'Pec deck / chest fly machine', category: 'machine', defaultAvailable: true },
  { id: 'lat_pulldown', name: 'Lat pulldown', category: 'machine', defaultAvailable: true },
  { id: 'seated_row_machine', name: 'Seated row machine', category: 'machine', defaultAvailable: true },
  { id: 'tbar_row', name: 'T-bar row', category: 'machine', defaultAvailable: true },
  { id: 'high_row_machine', name: 'Plate-loaded high/low row', category: 'machine', defaultAvailable: true },
  { id: 'shoulder_press_machine', name: 'Shoulder press machine', category: 'machine', defaultAvailable: true },
  { id: 'lateral_raise_machine', name: 'Lateral raise machine', category: 'machine', defaultAvailable: true },
  { id: 'rear_delt_machine', name: 'Rear delt / reverse pec deck', category: 'machine', defaultAvailable: true },
  { id: 'biceps_curl_machine', name: 'Biceps curl machine', category: 'machine', defaultAvailable: true },
  { id: 'triceps_machine', name: 'Triceps extension machine', category: 'machine', defaultAvailable: true },
  { id: 'assisted_pullup', name: 'Assisted pull-up / dip machine', category: 'machine', defaultAvailable: true },
  { id: 'leg_press', name: 'Leg press', category: 'machine', defaultAvailable: true },
  { id: 'hack_squat', name: 'Hack squat', category: 'machine', defaultAvailable: true },
  { id: 'leg_extension', name: 'Leg extension', category: 'machine', defaultAvailable: true },
  { id: 'leg_curl_seated', name: 'Seated leg curl', category: 'machine', defaultAvailable: true },
  { id: 'leg_curl_lying', name: 'Lying leg curl', category: 'machine', defaultAvailable: true },
  { id: 'hip_thrust_machine', name: 'Hip thrust machine', category: 'machine', defaultAvailable: true, note: 'Precor Booty Zone' },
  { id: 'glute_kickback', name: 'Glute kickback machine', category: 'machine', defaultAvailable: true, note: 'Precor Booty Zone' },
  { id: 'abductor_machine', name: 'Hip abductor machine', category: 'machine', defaultAvailable: true },
  { id: 'adductor_machine', name: 'Hip adductor machine', category: 'machine', defaultAvailable: true },
  { id: 'calf_raise_standing', name: 'Standing calf raise', category: 'machine', defaultAvailable: true },
  { id: 'calf_raise_seated', name: 'Seated calf raise', category: 'machine', defaultAvailable: true },
  { id: 'ab_crunch_machine', name: 'Ab crunch machine', category: 'machine', defaultAvailable: true },

  // ---- Cables ----
  { id: 'cable_station', name: 'Adjustable cable column', category: 'cable', defaultAvailable: true },
  { id: 'cable_crossover', name: 'Dual cable crossover', category: 'cable', defaultAvailable: true },
  { id: 'rope_attachment', name: 'Rope attachment', category: 'cable', defaultAvailable: true },
  { id: 'straight_bar_attachment', name: 'Straight / V-bar attachment', category: 'cable', defaultAvailable: true },
  { id: 'single_handle', name: 'Single D-handle', category: 'cable', defaultAvailable: true },
  { id: 'ankle_strap', name: 'Ankle strap', category: 'cable', defaultAvailable: true },

  // ---- Functional ----
  { id: 'landmine', name: 'Landmine attachment', category: 'functional', defaultAvailable: true },
  { id: 'resistance_bands', name: 'Resistance bands', category: 'functional', defaultAvailable: true },
  { id: 'trx', name: 'TRX / suspension trainer', category: 'functional', defaultAvailable: true },
  { id: 'med_ball', name: 'Medicine balls', category: 'functional', defaultAvailable: true },
  { id: 'plyo_box', name: 'Plyo box', category: 'functional', defaultAvailable: true },
  { id: 'ab_wheel', name: 'Ab wheel', category: 'functional', defaultAvailable: true },
  { id: 'dip_belt', name: 'Dip belt', category: 'functional', defaultAvailable: true },
  { id: 'mat', name: 'Exercise mat', category: 'functional', defaultAvailable: true },
  { id: 'bodyweight', name: 'Bodyweight only', category: 'functional', defaultAvailable: true },

  // ---- Cardio ----
  { id: 'treadmill', name: 'Treadmill', category: 'cardio', defaultAvailable: true },
  { id: 'upright_bike', name: 'Upright bike', category: 'cardio', defaultAvailable: true },
  { id: 'recumbent_bike', name: 'Recumbent bike', category: 'cardio', defaultAvailable: true },
  { id: 'elliptical', name: 'Elliptical / cross-trainer', category: 'cardio', defaultAvailable: true },
  { id: 'stairmaster', name: 'Stairmaster / stepper', category: 'cardio', defaultAvailable: true },
  { id: 'rower', name: 'Rowing machine', category: 'cardio', defaultAvailable: true },
  { id: 'air_bike', name: 'Air / assault bike', category: 'cardio', defaultAvailable: true },
  { id: 'ski_erg', name: 'SkiErg', category: 'cardio', defaultAvailable: false },

  // ---- Recovery (Galerija Centrs) ----
  { id: 'hydromassage', name: 'HydroMassage bed', category: 'recovery', defaultAvailable: true },
  { id: 'cryolounge', name: 'CryoLounge bed', category: 'recovery', defaultAvailable: true },
  { id: 'rollshaper', name: 'RollShaper roller', category: 'recovery', defaultAvailable: true },
  { id: 'cloudboots', name: 'Cloudboots compression', category: 'recovery', defaultAvailable: true },
  { id: 'theragun', name: 'Theragun', category: 'recovery', defaultAvailable: true },
  { id: 'foam_roller', name: 'Foam roller', category: 'recovery', defaultAvailable: true },
];

export const EQUIPMENT_BY_ID: Record<string, Equipment> = Object.fromEntries(
  EQUIPMENT.map((e) => [e.id, e]),
);

export const CATEGORY_LABEL: Record<EquipmentCategory, string> = {
  bar: 'Bars',
  freeweight: 'Free weights',
  bench: 'Benches',
  rack: 'Racks & frames',
  machine: 'Machines',
  cable: 'Cables',
  functional: 'Functional',
  cardio: 'Cardio',
  recovery: 'Recovery',
};

export const CATEGORY_ORDER: EquipmentCategory[] = [
  'bar',
  'freeweight',
  'bench',
  'rack',
  'machine',
  'cable',
  'functional',
  'cardio',
  'recovery',
];

export function defaultEquipmentIds(): string[] {
  return EQUIPMENT.filter((e) => e.defaultAvailable).map((e) => e.id);
}

export function equipmentName(id: string): string {
  return EQUIPMENT_BY_ID[id]?.name ?? id;
}
